// One-shot fetcher for the picker's currently-active wave.
// Returns { wave, lines, currentLine, progress } or nulls during
// loading / when the picker has no active wave.
//
// Pattern matches the rest of the codebase: raw useEffect + apiFetch
// (no React Query). Refetch is exposed so consumers can re-pull after
// state changes (wave start, wave complete) without unmounting.
import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "../api/apiFetch";

export interface ActiveWaveLine {
    line_id: number;
    sku: string;
    location: string;            // raw barcode — what the picker scans
    location_display?: string;   // aisle-bay-level for the hero, when joined
    // The location's aisle code and bay number on their own
    // (locations.aisle / locations.bay — bay is TEXT, "54"), when
    // joined. `aisle` groups the run's lines for the "Units left by
    // aisle" popup; `bay` is declared because the BE sends it, nothing
    // reads it yet.
    aisle?: string | null;
    bay?: string | number | null;
    // Actual high/low tier of the physical location (from locations.high_low).
    // May differ from the wave's tier — NULL high_low locations are eligible
    // for any wave, so a "Low" wave can route the picker through tier-agnostic
    // shelves. NULL when the join misses (legacy lines / unknown barcodes).
    location_high_low?: "high" | "low" | null;
    // "A1".."E6" — bay-position grid slot for the picker hero diagram.
    // Only rendered for low locations. NULL means "no diagram" (e.g.
    // high reserve, lanes, areas).
    bay_position?: string | null;
    product_barcode?: string | null;  // printed barcode on the product
    // The product's SECOND barcode, when it has more than one (primary =
    // products.barcode, extras = sku_barcodes). Shown under the SKU on
    // the picker hero so a product with two printed barcodes is easy to
    // recognise either way. NULL/absent when the product has only one
    // barcode — the hero then renders as before. Only ever the second;
    // third/fourth are not surfaced.
    product_barcode_secondary?: string | null;
    // EVERY barcode the product scans as — primary (products.barcode)
    // first, then sku_barcodes extras in insertion order, de-duplicated.
    // Drives the action bar's "other barcodes" popup (yellow + badge).
    // Optional so older BE payloads simply hide the affordance.
    product_barcodes?: string[] | null;
    product_title?: string | null;    // human-readable name (e.g. "Tefal Easy Fry")
    // Catalogue image, plus a small (224×224) thumbnail derived at
    // upload time. The picker hero prefers thumbnail_url for fast load
    // on handhelds; image_url is the fallback if thumbnail isn't set.
    product_image_url?: string | null;
    product_thumbnail_url?: string | null;
    // Product dimensions, straight off the catalogue. Feed the Large
    // roll-cage estimate (see picking-shared/rollCages.ts) and nothing
    // else. NUMERIC columns, so they arrive as STRINGS — always coerce.
    // Absent/null means the SKU has no recorded size: its units are
    // counted as "missing measurements" rather than treated as zero
    // volume without saying so.
    length_cm?: number | string | null;
    width_cm?: number | string | null;
    height_cm?: number | string | null;
    walk_order: number;
    qty_required: number;
    qty_picked: number;
    status: "pending" | "picking" | "picked" | "missing" | "flagged";
    sort_index: number;
    // Owning order — exact for Light/Large (one order per line),
    // arbitrary for P&S (consolidated lines span orders). Read only by
    // Large's same-order sibling-sweep check in validateScan; absent
    // on older BE payloads, in which case Large stays strict.
    order_id?: string | null;
    // Combined quantities for a SKU split across several lines at one
    // physical location (the allocator splits one SKU across multiple
    // inventory_detail batches → one wave line per batch). The hero
    // shows ONE continuous counter for the whole pile instead of a
    // 7/7-then-0/3 reset, computed as
    // `group_qty_prior_picked + qty_picked` / `group_qty_required`.
    //   • group_qty_required     — Σ qty_required across all lines at
    //     this (sku, location). Equals qty_required when not split.
    //   • group_qty_prior_picked — Σ qty_picked of the OTHER lines in
    //     the group (0 when not split). Server-computed; the local-first
    //     path recomputes it from the live line set as picks land.
    // Both optional so older BE bundles (no group fields) fall back to
    // the plain per-line qty.
    group_qty_required?: number;
    group_qty_prior_picked?: number;
    // Location-level totals — Σ qty_required / Σ qty_picked over EVERY
    // wave line at this line's physical location (all SKUs, all
    // statuses), plus the count of DISTINCT SKUs in that set. Drives
    // the "Units at this location" counter in the also-at-location
    // panel — only shown when the location holds more than one SKU
    // (sku_count > 1). Server-computed alongside the group totals; the
    // local-first path recomputes them from the live line set as picks
    // land (see locationTotalsFor). Optional so older BE bundles (no
    // location fields) simply hide the counter.
    location_qty_required?: number;
    location_qty_picked?: number;
    location_sku_count?: number;
    // Other open lines (status in pending/picking) at the same physical
    // location — drives the "Also at this location" panel on the
    // picker hero. Server-computed in loadActiveWave so the FE doesn't
    // recompute on every render. Always an array, possibly empty.
    other_lines_at_location?: Array<{
        sku: string;
        title: string | null;
        thumbnail_url: string | null;
        // Primary product barcode — optional so older BE payloads
        // (no barcode field) keep working; the panel falls back to SKU.
        barcode?: string | null;
        qty_required: number;
    }>;
}

export interface ActiveWaveHeader {
    wave_id: number;
    warehouse: string;
    pick_type: "light" | "pick_and_sort" | "large";
    // Pick & Sort variants (see pickTypeLabel.ts): sort_station is
    // "put_wall" for a Putwall wave, wave_size is "large" for a two-bay
    // wave. Plain values for Single / Large; an older BE payload omits
    // them — treat absent as plain.
    sort_station?: "pick_and_sort" | "put_wall" | null;
    wave_size?: "small" | "large" | null;
    // Paired wave (migration 223): which half this is and who is on the
    // other one. segment_count > 1 also tells the session hook that the
    // BE decides when the wave is complete — the other half may be
    // folded onto the end of this one when the lines run out. Absent on
    // an older BE / a solo wave: 1 of 1, no sibling.
    sort_group_id?: number | null;
    segment_no?: number;
    segment_count?: number;
    sibling_picker_name?: string | null;
    sibling_status?: string | null;
    sibling_awaiting_picker?: boolean | null;
    tier: "high" | "low";
    status: "building" | "active" | "paused" | "completed" | "released";
    current_picker_uid: number | null;
    current_picker_name: string | null;
    alloc_method: "fifo" | "fastest";
    started_at: string | null;
    last_activity_at: string;
    // Currently-open tote for this wave (null until first scan, swaps
    // when picker scans a different tote). Surfaced so the FE can
    // resume cleanly after reload + drive the awaiting-first-tote phase.
    current_tote_id: string | null;
    total_totes: number;
    // Large waves: the anchor barcode the wave was built around (the
    // picker's location scan). Stamped at build by the seeded /start;
    // legacy waves fill it via the starting-location scan. Pick & Sort
    // / Single leave it NULL — they don't use the anchoring flow.
    starting_location: string | null;
    // Build-time filters (Large next-order prompt restarts under the
    // SAME criteria the picker chose on the selector — reading them off
    // the wave header keeps them correct across reload/resume).
    // Optional: an older BE payload may omit them; callers fall back
    // to "all" / null. channel_filter is NULL when the picker chose
    // "all" (the BE stores it that way).
    pick_queue?: number | null;
    age_filter?: string | null;
    delivery_filter?: string | null;
    channel_filter?: string | null;
    due_filter?: boolean | null;
    age_from?: string | null;
    age_to?: string | null;
    // Resume-tote verification: set by the BE when a paused wave is
    // resumed and a tote was open at pause. Every scan is rejected
    // until the picker re-scans THIS tote (proving they hold the
    // wave's partially-picked items); the matching scan clears it.
    pending_resume_tote?: string | null;
    // From `settings.picking_require_location_scan` for this
    // warehouse. When false the FE skips the per-line location-scan
    // phase and goes straight from tote/item-complete to the next
    // item scan. Applies to all pick types.
    require_location_scan: boolean;
    // Monotonic counter that bumps on any structural change to the
    // wave's lines (JIT order append, missing-item re-slot). Powers the
    // FE lookahead: if a response's waveVersion is higher than the
    // version the FE used to enqueue its lookahead line, the queue is
    // dropped and re-seeded from the response.
    wave_version: number;
    // When TRUE, this warehouse has the local-first scan path enabled.
    // The FE owns scan state locally via usePickingSession; scans are
    // validated client-side and POSTed fire-and-forget. When FALSE
    // (default), the legacy per-scan round-trip path runs.
    picking_local_first_enabled: boolean;
    // UI redesign settings — drives the unified PickingScreen hero.
    // show_product_image false → hero hides the image slot entirely.
    // other_skus_count is 0-10; 0 hides the "Also at this location"
    // panel; higher values render that many rows then "+N more".
    picking_show_product_image?: boolean;
    // How long the full-screen location-change takeover stays up (ms).
    // From settings.picking_location_change_ms; PickingScreen falls
    // back to LOCATION_CHANGE_MS when absent. 0 disables the takeover.
    picking_location_change_ms?: number;
    // How long the post-dismiss "new location" highlight holds (ms).
    // From settings.picking_new_location_highlight_ms; falls back to
    // NEW_LOCATION_HIGHLIGHT_MS. 0 disables the highlight.
    picking_new_location_highlight_ms?: number;
    picking_other_skus_count?: number;
    // Roll-cage estimate (Large only). utilisation is
    // settings.picking_roll_cage_utilisation_pct — how full a cage
    // realistically gets, per warehouse; NULL/absent means the
    // warehouse has the estimate switched off and the screen hides it.
    // roll_cage_cbm is settings.picking_roll_cage_cbm — the cage's
    // physical capacity at THIS warehouse, already coalesced BE-side to
    // the standard 0.72 CBM cage when it was never set. Shipped here so
    // the device never keeps its own copy of either figure.
    picking_roll_cage_utilisation_pct?: number | string | null;
    roll_cage_cbm?: number | string | null;
}

export interface ActiveWaveProgress {
    // Item-grained progress: total units to pick across the wave and
    // how many have been scanned so far. Bar = itemsPicked / totalItems
    // so it advances on every accepted item scan instead of jumping
    // only when a whole location is complete.
    totalItems: number;
    itemsPicked: number;
    // Large waves only: progress scoped to the order the picker is on
    // (open tote's order, or the next queued order at the tote prompt).
    // Large is one order per tote but its JIT lookahead appends the
    // NEXT order's lines mid-pick, so the wave counter jumps — a
    // 37-unit order read "37/71" and was reported as a bad merge.
    // When present, the screen shows these instead.
    orderTotalItems?: number;
    orderItemsPicked?: number;
}

export interface ActiveWaveResponse {
    wave: ActiveWaveHeader | null;
    lines: ActiveWaveLine[];
    currentLine: ActiveWaveLine | null;
    // Back-compat: the next line after the current one. Equal to
    // `lookahead[0]` when the queue is non-empty. Older code reads
    // this; new code should prefer `lookahead`.
    //
    // OPTIONAL: under local-first the BE omits this — the device owns the
    // full `lines` array and derives the next line locally (findCurrentLine).
    // Only the legacy (local-first-off) path still sends it. picking.tsx /
    // applyResponse already default to `[]` when it's absent.
    nextLine?: ActiveWaveLine | null;
    // FE queue of upcoming pending/picking lines after the current one.
    // Length is bounded by settings.picking_lookahead_orders (1-5, default 2)
    // — mirrors Shelf/Forecast Drop's `lookaheadQueue` so the hero
    // swaps instantly when a line completes (no round-trip wait).
    //
    // OPTIONAL: omitted under local-first (see nextLine). The legacy path
    // still sends it to drive its optimistic hero swap.
    lookahead?: ActiveWaveLine[];
    progress: ActiveWaveProgress | null;
    waveVersion: number | null;
    // Local-first addition. `barcodeMap` maps every scannable value
    // (sku / master_sku / products.barcode / sku_barcodes.barcode) that
    // resolves to a SKU present in this wave to the SET of SKUs it
    // resolves to, so the FE can validate item scans without a per-scan
    // round-trip. Tote scans are NOT pre-mapped — a barcode that's
    // neither the current location nor a known item is treated as a
    // tote candidate and resolved per-scan by the BE. Old paths ignore.
    barcodeMap?: Record<string, string[]>;
}

export const useActiveWave = () => {
    const [data, setData] = useState<ActiveWaveResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    // Count consecutive 500 failures so the picker isn't stranded
    // forever on a broken wave. After three back-to-back 500s we fire
    // /picking/wave/pause-mine to drop the wave into the paused list,
    // then refetch — the picker sees their wave under "Paused" and
    // can resume normally once the underlying issue is sorted.
    const consecutive5xxRef = useRef(0);

    const refetch = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch("/picking/active", {});
            if (!res.ok) {
                if (res.status >= 500) {
                    consecutive5xxRef.current += 1;
                    if (consecutive5xxRef.current >= 3) {
                        // Try the recovery endpoint, then refetch
                        // without the 5xx counter so we don't loop.
                        try {
                            await apiFetch("/picking/wave/pause-mine", { method: "POST" });
                        } catch { /* best effort */ }
                        consecutive5xxRef.current = 0;
                    }
                }
                throw new Error(`HTTP ${res.status}`);
            }
            consecutive5xxRef.current = 0;
            const body: ActiveWaveResponse = await res.json();
            setData(body);
            setError(null);
        } catch (e) {
            setError(e instanceof Error ? e.message : "fetch failed");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refetch();
    }, [refetch]);

    return { data, loading, error, refetch, setData };
};
